"use client";

/**
 * P2-04 / P5 / P18-19 · The card's two decorations, in one island module.
 *
 * `<CardStatusBadge>` is the single status badge (P2-DB04 r2 §02 precedence:
 * out-of-stock › sale › low-stock › new). `<CardRating>` is the social-proof
 * line from the published-only review denorm.
 *
 * Both are client components for the same small reason — one translates two of
 * its four readings, the other asks a store flag and renders `<StarRating>`,
 * which is itself a client component — and both are handed values the pure
 * helpers (`deriveCardStatus`, `discountPercent`) have already decided, so
 * neither needs the card. Keeping them in one module keeps the card's
 * client-reference count (and so the route's bundle) down.
 */
import { useOptionalFeatureFlag } from "~/components/feature-flags/provider";
import { StarRating } from "~/components/storefront/reviews/star-rating";
import { useTranslator } from "~/i18n/provider";

import type { CardStatus } from "./derive";

export interface CardStatusBadgeProps {
  status: CardStatus;
}

export function CardStatusBadge({ status }: CardStatusBadgeProps) {
  const t = useTranslator();
  if (status === null) return null;
  // Tread · pill badges: Sale is Tyre on Brass, New is white on Tyre, Last one
  // is an outline. Sale reads "Sale", not a percentage; the card link's name
  // already carries the was/now prices. Plain spans rather than <Badge>: its
  // base padding and size would compete with these (classes aren't merged).
  const pill = (tone: string, label: string) => (
    <span
      className={`inline-flex items-center rounded-full px-2.5 py-1 text-xs font-semibold leading-none md:px-3 md:py-1.5 md:text-[13px] ${tone}`}
    >
      {label}
    </span>
  );
  switch (status) {
    case "sold-out":
      return pill("bg-surface-2 text-fg-muted", t("product.outOfStock"));
    case "last-one":
      return pill(
        "border-[1.5px] border-secondary bg-surface text-fg",
        t("product.lastOne"),
      );
    case "sale":
      return pill("bg-accent text-accent-fg", t("product.sale"));
    case "new":
      return pill("bg-secondary text-secondary-fg", t("product.new"));
  }
}

export interface CardRatingProps {
  ratingAverage: number | null | undefined;
  ratingCount: number | undefined;
}

export function CardRating({ ratingAverage, ratingCount }: CardRatingProps) {
  const enabled = useOptionalFeatureFlag("reviews");
  // Loose checks on purpose: pre-P5 cached payloads carry neither field.
  if (!enabled || !ratingCount || ratingAverage == null) return null;
  return (
    <span className="mt-1 flex items-center gap-1.5">
      <StarRating value={ratingAverage} size="sm" />
      <span className="text-[11px] tabular-nums text-fg-muted">
        ({ratingCount})
      </span>
    </span>
  );
}
